import { useState } from 'react';

import type { TicketDecisionView, TicketReadinessView } from '@nightshift/ui-types';

import { Spinner } from '../../components/primitives.tsx';
import { api } from '../../live/api.ts';
import { useStore } from '../../live/context.tsx';
import { useTimeFormat } from '../../lib/display.ts';
import { observedTime } from '../../lib/format.ts';
import './readiness.css';

const CHECK_LABEL: Record<TicketReadinessView['checks'][number]['id'], string> = {
  'acceptance-source': 'Acceptance',
  'duplicated-requirements': 'One contract',
  decisions: 'Decisions',
  dependencies: 'Dependencies',
  'evidence-plan': 'Evidence plan',
};

const STATUS_LABEL: Record<TicketReadinessView['checks'][number]['status'], string> = {
  ok: 'ok',
  warn: 'attention',
  block: 'blocked',
};

/**
 * Is this ticket ready to implement, and what would make it so.
 *
 * Every row is one of `assessTicketReadiness`'s checks, verbatim; the panel adds nothing of its
 * own. The one action it offers is the acceptance move, which is a mechanical copy the server
 * previews, and it goes through the ordinary update so the contract revision moves as it would
 * for a hand edit.
 */
export function ReadinessSection({
  ticketKey,
  ticketVersion,
  readiness,
  decisions,
  now,
  onReloaded,
}: {
  ticketKey: string;
  ticketVersion: number;
  readiness: TicketReadinessView;
  decisions: readonly TicketDecisionView[];
  now: number;
  onReloaded: () => Promise<void>;
}) {
  const store = useStore();
  const format = useTimeFormat();
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const preview = readiness.acceptancePreview;

  async function adopt() {
    setBusy(true);
    try {
      await api.adoptAcceptance(ticketKey, ticketVersion);
      store.toast('good', 'Acceptance criteria moved into the Acceptance field');
      await onReloaded();
    } catch (error) {
      store.toast('bad', error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function record() {
    setBusy(true);
    try {
      await api.recordDecision(ticketKey, { question: question.trim(), answer: answer.trim(), expectedVersion: ticketVersion });
      store.toast('good', 'Decision recorded');
      setQuestion('');
      setAnswer('');
      setRecording(false);
      await onReloaded();
    } catch (error) {
      store.toast('bad', error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="readiness" aria-label="Readiness">
      <div className="readiness-head">
        <h3 className="drawer-section-kicker">Readiness</h3>
        <span className={`readiness-verdict is-${readiness.ready ? 'ready' : 'blocked'}`}>
          {readiness.ready ? 'Ready to implement' : 'Not ready'}
        </span>
      </div>
      <ul className="readiness-list">
        {readiness.checks.map((check) => (
          <li key={check.id} className={`readiness-row is-${check.status}`} data-check={check.id}>
            <div className="readiness-row-head">
              <strong>{CHECK_LABEL[check.id]}</strong>
              <span className={`readiness-status is-${check.status}`}>{STATUS_LABEL[check.status]}</span>
            </div>
            <p className="readiness-detail">{check.detail}</p>
          </li>
        ))}
      </ul>
      {preview ? (
        <div className="readiness-preview">
          <p className="dim">
            Move {preview.moved.length === 1 ? 'this criterion' : `these ${preview.moved.length} criteria`} out of
            Problem, verbatim:
          </p>
          <ul className="readiness-moved">
            {preview.moved.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
          <button type="button" className="btn btn-sm btn-accent" disabled={busy} onClick={() => void adopt()}>
            {busy ? <Spinner /> : null} Move into Acceptance
          </button>
        </div>
      ) : null}

      <div className="readiness-decisions">
        <div className="readiness-head">
          <h3 className="drawer-section-kicker">Decisions</h3>
          {!recording ? (
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setRecording(true)}>
              Record a decision
            </button>
          ) : null}
        </div>
        {decisions.length === 0 && !recording ? (
          <p className="dim">No product decisions recorded. An answer to an agent's question round lands here.</p>
        ) : null}
        {decisions.length > 0 ? (
          <ol className="readiness-decision-list">
            {decisions.map((decision) => (
              <li key={decision.id} className="readiness-decision">
                <p className="readiness-decision-q">{decision.question}</p>
                <p className="readiness-decision-a">{decision.answer}</p>
                <p className="dim readiness-decision-meta">
                  {decision.actor} · revision {decision.contractRevision} ·{' '}
                  <time dateTime={new Date(decision.createdAt).toISOString()}>
                    {observedTime(decision.createdAt, now, format)}
                  </time>
                </p>
              </li>
            ))}
          </ol>
        ) : null}
        {recording ? (
          <form
            className="readiness-record"
            onSubmit={(event) => {
              event.preventDefault();
              void record();
            }}
          >
            <label>
              <span className="dim">Question</span>
              <input value={question} disabled={busy} onChange={(event) => setQuestion(event.target.value)} />
            </label>
            <label>
              <span className="dim">Answer</span>
              <textarea rows={3} value={answer} disabled={busy} onChange={(event) => setAnswer(event.target.value)} />
            </label>
            <div className="readiness-record-actions">
              <button type="submit" className="btn btn-sm btn-accent" disabled={busy || !question.trim() || !answer.trim()}>
                {busy ? <Spinner /> : null} Save decision
              </button>
              <button type="button" className="btn btn-sm btn-quiet" disabled={busy} onClick={() => setRecording(false)}>
                Cancel
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </section>
  );
}
